import { Button } from "../../components/ui/button/index.js";
import { SimpleSelect } from "../../components/ui/select/index.js";
import { cn } from "../../lib/utils.js";
import {
  SUPER_ADMIN_TABLE_PAGE_SIZES,
  type SuperAdminTablePageSize,
} from "./super-admin-table-presentation.js";

/**
 * Cursor-list footer: page size + first/next. No fake total/page-of-N
 * (Super Admin list APIs do not return totalCount).
 */
export function SuperAdminCursorPaginationBar({
  pageSize,
  onPageSizeChange,
  hasNextPage,
  isFirstPage,
  onNextPage,
  onFirstPage,
  pageSizeLabel,
  nextLabel,
  firstLabel,
  showingCountLabel,
  itemCount,
  testIdPrefix,
  className,
}: {
  pageSize: SuperAdminTablePageSize;
  onPageSizeChange: (size: SuperAdminTablePageSize) => void;
  hasNextPage: boolean;
  isFirstPage: boolean;
  onNextPage: () => void;
  onFirstPage: () => void;
  pageSizeLabel: string;
  nextLabel: string;
  firstLabel: string;
  /** Optional “Showing {{count}}” when totals are unavailable. */
  showingCountLabel?: string;
  itemCount?: number;
  testIdPrefix: string;
  className?: string;
}): React.JSX.Element {
  const showFooter = hasNextPage || !isFirstPage || itemCount !== undefined;

  if (!showFooter) {
    return <></>;
  }

  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border/60 bg-surface px-3 py-2",
        className,
      )}
      data-testid={`${testIdPrefix}-pagination`}
    >
      <p className="m-0 text-[0.75rem] text-muted tabular-nums">
        {showingCountLabel !== undefined && itemCount !== undefined
          ? showingCountLabel.replace("{{count}}", String(itemCount))
          : null}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-[0.75rem] text-muted">
          <span className="sr-only md:not-sr-only">{pageSizeLabel}</span>
          <SimpleSelect
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value) as SuperAdminTablePageSize)}
            ariaLabel={pageSizeLabel}
            triggerClassName="h-8 w-[4.25rem] min-w-0 text-start text-[0.75rem]"
            options={SUPER_ADMIN_TABLE_PAGE_SIZES.map((size) => ({
              value: String(size),
              label: String(size),
            }))}
          />
        </label>
        {!isFirstPage ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8"
            onClick={onFirstPage}
            data-testid={`${testIdPrefix}-first`}
          >
            {firstLabel}
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8"
          disabled={!hasNextPage}
          onClick={onNextPage}
          data-testid={`${testIdPrefix}-next`}
        >
          {nextLabel}
        </Button>
      </div>
    </div>
  );
}
